import { createContext, useContext, useEffect, useState, createElement } from "react";
import { toast } from "sonner";
import { api, safeGet } from "./api";
import { PRODUCTS } from "./content";
import { track, getUtm } from "./track";
import { trackingTokens, shopifyAddToCart, initShopifySession } from "./shopifyAnalytics";
import { klaviyoAddedToCart } from "./klaviyo";
import { getCountry, chooseCountry, formatMoney } from "./geo";

const ShopContext = createContext({ products: PRODUCTS, connected: false, buy: async () => {}, money: formatMoney,
  priceOf: (role, installment) => formatMoney(installment ? PRODUCTS[role].installment : PRODUCTS[role].price) });

const merge = (remote) => {
  const out = {};
  Object.entries(PRODUCTS).forEach(([role, base]) => {
    const r = remote?.[role];
    const variants = Array.isArray(r?.variants) ? r.variants : [];
    const full = variants.find((v) => !v.is_installment) || variants[0];
    out[role] = {
      ...base,
      price: full?.price ?? base.price,
      installment: full ? Math.round(full.price * 50) / 100 : base.installment,
      currency: full?.currency ?? "CHF",
      variants,
      sellingPlans: Array.isArray(r?.selling_plans) ? r.selling_plans : [],
      gallery: Array.isArray(r?.images) && r.images.length ? r.images.map((u) => `${u}&width=1600`) : [base.image],
      live: Boolean(full),
      handle: r?.handle,
      shopifyId: r?.shopify_id,
    };
  });
  return out;
};

const CART_KEY = "fm_cart";
const PRODUCTS_KEY = "fm_products";
const readJson = (k) => { try { return JSON.parse(localStorage.getItem(k) || "null"); } catch { return null; } };
const writeJson = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* storage unavailable */ } };
const readCartId = () => { try { return localStorage.getItem(CART_KEY); } catch { return null; } };
const saveCartId = (id) => { try { id ? localStorage.setItem(CART_KEY, id) : localStorage.removeItem(CART_KEY); } catch { /* storage unavailable */ } };

export const ShopProvider = ({ children }) => {
  // Last prices seen (same country) are shown instantly to avoid a flash of the wrong currency.
  const [state, setState] = useState(() => ({ products: merge(readJson(PRODUCTS_KEY)?.products), connected: false }));
  const [cart, setCart] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [country, setCountry] = useState(null);

  const loadFor = (c) => {
    setCountry(c);
    const q = c ? `?country=${c}` : "";
    safeGet(`/products${q}`, null).then((d) => {
      setState({ products: merge(d?.products), connected: Boolean(d?.configured && !d?.error) });
      if (d?.products) writeJson(PRODUCTS_KEY, { products: d.products });
    });
    const id = readCartId();
    if (id && api) {
      api.post("/cart/get", { cart_id: id, country: c || undefined }).then((r) => {
        const cc = r.data?.cart;
        if (cc && cc.totalQuantity > 0) setCart(cc); else { setCart(null); saveCartId(null); }
      }).catch(() => saveCartId(null));
    }
  };

  useEffect(() => {
    getUtm();
    initShopifySession();
    getCountry().then(loadFor);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const changeCountry = (c) => loadFor(chooseCountry(c));

  const ctry = country || undefined;
  const money = (amount, currency) => formatMoney(amount, currency || state.products.ebook?.currency);
  const priceOf = (role, installment = false) => {
    const p = state.products[role];
    return formatMoney(installment ? p.installment : p.price, p.currency);
  };

  const lineFor = (role, installment) => {
    const p = state.products[role];
    const full = p.variants.find((v) => !v.is_installment) || p.variants[0];
    const inst = p.variants.find((v) => v.is_installment);
    const variant = installment && inst ? inst : full;
    if (!variant) return null;
    return { variant_id: variant.id, selling_plan_id: installment && !inst ? p.sellingPlans[0]?.id : undefined, quantity: 1 };
  };

  const roleOfLine = (line) => {
    const h = line?.merchandise?.product?.handle;
    return Object.keys(state.products).find((k) => state.products[k].handle === h);
  };

  const buy = async (role, { installment = false } = {}) => {
    const p = state.products[role];
    const line = lineFor(role, installment);
    if (!api || !line) {
      toast("Checkout is not connected yet", { description: "The shop connection is being set up. Please try again shortly." });
      return;
    }
    track("add_to_cart", { content_name: p.name, value: installment ? p.installment * 2 : p.price, currency: p.currency });
    const nodes = cart?.lines?.nodes ?? [];
    if (role !== "bundle" && nodes.some((l) => roleOfLine(l) === "bundle")) {
      toast("Already included in your Bundle");
      setCartOpen(true);
      return;
    }
    try {
      const replace = nodes.filter((l) => roleOfLine(l) === role || (role === "bundle" && ["ebook", "workbook"].includes(roleOfLine(l))));
      let id = cart?.id;
      const tracking = await trackingTokens();
      const country = (await getCountry()) || undefined;
      for (const l of replace) {
        const r = await api.post("/cart/update", { cart_id: id, line_id: l.id, quantity: 0, tracking, country });
        id = r.data?.cart?.id ?? id;
      }
      const res = id
        ? await api.post("/cart/add", { cart_id: id, lines: [line], tracking, country })
        : await api.post("/cart/create", { lines: [line], utm: getUtm(), tracking, country });
      const c = res.data?.cart;
      if (c) {
        setCart(c); saveCartId(c.id); setCartOpen(true);
        const variant = p.variants.find((v) => v.id === line.variant_id);
        shopifyAddToCart(p, variant, c.id);
        klaviyoAddedToCart(p, variant?.price ?? p.price, c);
      }
    } catch (e) {
      toast.error("Could not add to cart", { description: e?.response?.data?.detail || "Please try again." });
    }
  };

  const updateLine = async (lineId, quantity) => {
    try {
      const res = await api.post("/cart/update", { cart_id: cart.id, line_id: lineId, quantity, tracking: await trackingTokens(), country: ctry });
      setCart(res.data?.cart ?? null);
    } catch {
      toast.error("Could not update the cart");
    }
  };

  const checkout = () => {
    if (!cart?.checkoutUrl) return;
    let host = "";
    try { host = new URL(cart.checkoutUrl).host; } catch { host = ""; }
    if (host === window.location.host) {
      track("checkout_misconfigured", { host });
      toast.error("Checkout is temporarily unavailable", { description: "Please try again in a few minutes." });
      return;
    }
    track("begin_checkout", { value: Number(cart.cost?.totalAmount?.amount ?? 0), currency: cart.cost?.totalAmount?.currencyCode });
    window.location.href = cart.checkoutUrl;
  };

  const value = { ...state, buy, cart, cartOpen, setCartOpen, updateLine, checkout, roleOfLine, money, priceOf, country, changeCountry };
  return createElement(ShopContext.Provider, { value }, children);
};

export const useShop = () => useContext(ShopContext);
